import { PageBack, PageHeadContainer, PageTitle } from 'components/PageHead';
import UserInformationForm, {
  Errors,
  initialErrors,
  State,
} from 'components/UserInformation/UserInformationForm';
import { GenderValues } from 'enums/Genders';
import { professionalSituationRequiresDetails } from 'enums/ProfessionalSituation';
import { FC, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import useSWR, { mutate } from 'swr';
import User from 'types/User';
import privateAPI from 'utils/privateAxiosInstance';

const Title = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-start;
  margin-bottom: 0.5rem;
`;

const initialState: State = {
  gender: 'NOT_SPECIFIED' as GenderValues,
  firstName: '',
  lastName: '',
  email: '',
  organism: '',
  professionalSituation: '',
  priority: false,
  phone: '',
  address: '',
  postalCode: '',
  city: '',
  region: '',
  country: '',
  dateOfBirth: '',
  isMetropolitan: false,
  presentation: '',
  comments: '',
};

const EditInformationPage: FC = () => {
  const [state, setState] = useState<State>(initialState);
  const [errors, setErrors] = useState<Errors>(initialErrors);
  const [loading, setLoading] = useState<boolean>(false);

  const { data }: { data?: User } = useSWR('/auth/detailed-profile');
  useEffect(() => {
    data &&
      setState({
        gender: data.gender || 'NOT_SPECIFIED',
        firstName: data.firstName || '',
        lastName: data.lastName || '',
        // We have to replace the spaces manually because the email input automatically removes them but does not call onChange.
        // Thus, the displayed value was always correct, but the on in the state could be wrong.
        email: data.email?.replace(' ', '') || '',
        organism: data.organism || '',
        professionalSituation: data.professionalSituation || '',
        priority: data.priority || false,
        phone: data.phone || '',
        address: data.address || '',
        postalCode: data.postalCode || '',
        city: data.city || '',
        region: data.region || '',
        country: data.country || '',
        isMetropolitan: !!data.isMetropolitan,
        dateOfBirth: data.dateOfBirth
          ? new Date(data.dateOfBirth).toISOString().slice(0, 10)
          : '',
        presentation: data.presentation || '',
        comments: data.comments || '',
      });
  }, [data]);

  const navigate = useNavigate();
  const handleSubmit: React.FormEventHandler = (e) => {
    e.preventDefault();

    const isMentor = !!data?.UserRole?.some(
      (userRole) => userRole.role === 'MENTOR',
    );

    if (isMentor && !state.professionalSituation) {
      try {
        window.scrollTo({ top: 0 });
      } catch (error) {
        console.error('Erreur de scroll:', error);
      }
      return setErrors({
        ...initialErrors,
        professionalSituation: true,
      });
    }

    if (
      isMentor &&
      professionalSituationRequiresDetails(state.professionalSituation) &&
      !state.organism.trim()
    ) {
      try {
        window.scrollTo({ top: 0 });
      } catch (error) {
        console.error('Erreur de scroll:', error);
      }
      return setErrors({
        ...initialErrors,
        organism: true,
      });
    }

    if (
      !isMentor &&
      [state.address, state.region, state.postalCode, state.city, state.country].some(
        (value) => !value?.trim(),
      )
    ) {
      window.scrollTo({ top: 0 });
      return setErrors({
        ...initialErrors,
        address: true,
      });
    }

    setLoading(true);

    const newDateOfBirth = new Date(state.dateOfBirth);

    privateAPI
      .post('/auth/edit-profile', {
        ...state,
        dateOfBirth:
          newDateOfBirth.toString() === 'Invalid Date' ? null : newDateOfBirth,
        // La situation actuelle ne concerne que les mentors.
        professionalSituation: isMentor
          ? state.professionalSituation || null
          : undefined,
        organism:
          isMentor &&
          !professionalSituationRequiresDetails(state.professionalSituation)
            ? null
            : state.organism.trim(),
      })
      .then(() => {
        setLoading(false);
        mutate('/auth/profile');
        mutate('/auth/detailed-profile');
        navigate('/settings/');
      })
      .catch((err) => {
        setLoading(false);
        const fields: string[] = err?.response?.data?.fields || [];
        if (
          ['address', 'region', 'postalCode', 'city', 'country'].some((field) =>
            fields.includes(field),
          )
        ) {
          setErrors({
            ...initialErrors,
            address: true,
          });
        } else if (fields.includes('email')) {
          setErrors({
            ...initialErrors,
            email: true,
          });
        } else {
          setErrors({
            ...initialErrors,
            general: true,
          });
        }
        window.scrollTo({ top: 0 });
      });
  };

  return (
    <>
      <PageHeadContainer>
        <PageTitle>
          <div>
            <Title>
              <PageBack href={`/settings/`} />
              <h1>Modifier les informations</h1>
            </Title>
          </div>
        </PageTitle>
      </PageHeadContainer>
      <UserInformationForm
        {...{
          state,
          setState,
          errors,
          setErrors,
          loading,
        }}
        variant={
          data?.UserRole?.some((userRole) => userRole.role === 'MENTOR')
            ? 'mentor'
            : 'young'
        }
        onSubmit={handleSubmit}
        onCancel={() => navigate('/settings/')}
      />
    </>
  );
};

export default EditInformationPage;
